/**
 * Caixa de diálogo central, sobre um véu escuro, onde abre todo formulário de
 * cadastro.
 *
 * Papel: os `Dialog*` sobre o `Dialog` do `radix-ui`, que continua dono do
 * comportamento — o foco preso dentro da caixa, o `Esc` e o clique no véu que
 * a fecham, e o foco devolvido a quem a abriu. O que este arquivo decide é o
 * desenho: a caixa branca de 560 px centrada na janela, com rolagem própria
 * quando passa de 90% da altura, e as classes `CLASSES_*` que o
 * `alert-dialog.tsx` também usa, para a confirmação sair do mesmo molde.
 *
 * Como usar: a página controla `open` e `onOpenChange` do `Dialog`, e dentro do
 * `DialogContent` vêm o `DialogHeader`, com o `DialogTitle`, e o formulário,
 * que termina no `DialogFooter` com os botões. O `DialogTitle` é o nome
 * acessível da caixa — o texto que `getByRole('dialog', { name })` procura —,
 * e uma caixa sem ele fica anunciada como um diálogo sem nome. O
 * `DialogContent` já traz o padding e a rolagem, e por isso o formulário de
 * dentro não deverá abrir o próprio `overflow-y-auto` nem o próprio padding:
 * a caixa passaria a ter duas barras de rolagem, uma dentro da outra, e o
 * dobro da margem.
 *
 * A caixa nunca passa da largura da janela menos os 20 px de margem do véu de
 * cada lado: em tela estreita ela encolhe, e a grade de duas colunas do
 * formulário de dentro encolhe junto. A grade deverá ser `grid-cols-2`, que o
 * Tailwind escreve como `minmax(0, 1fr)`: com `1fr` puro, o texto sem quebra de
 * um `SelectTrigger` alargaria a coluna para além da caixa, e a caixa ganharia
 * rolagem horizontal.
 */
import { type ComponentProps } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@/shared/lib/utils';

/**
 * O véu que cobre a janela e centraliza a caixa. A caixa é filha dele, e não
 * irmã, para que o `p-5` seja a margem mínima entre a caixa e a borda da
 * janela em qualquer largura.
 */
export const CLASSES_DO_VEU = 'fixed inset-0 z-50 flex items-center justify-center bg-sobreposicao p-5';

/** A caixa branca, com a rolagem própria a partir de 90% da altura da janela. */
export const CLASSES_DA_CAIXA =
  'max-h-[90vh] w-[560px] max-w-full overflow-y-auto rounded-sm border border-linha-forte bg-superficie p-8 text-foreground outline-none';

/** O bloco do título, que guarda os 22 px até o conteúdo da caixa. */
export const CLASSES_DO_CABECALHO = 'mb-[22px] flex flex-col gap-3.5';

/** O título serifado da caixa. */
export const CLASSES_DO_TITULO = 'font-serif text-[20px] font-medium text-foreground';

/** A frase curta que explica a caixa, logo abaixo do título. */
export const CLASSES_DA_DESCRICAO = 'text-[12.5px] text-texto-medio';

/** A linha de botões, à direita, que quebra em vez de estourar a largura. */
export const CLASSES_DO_RODAPE = 'mt-[26px] flex flex-wrap justify-end gap-3';

/**
 * A raiz da caixa de diálogo, que guarda se ela está aberta.
 *
 * @param props - As props do `Dialog.Root` do Radix. `open` e `onOpenChange`
 *   ficam com a página, que é quem sabe qual registro está em edição; o
 *   `onOpenChange(false)` chega tanto pelo `Esc` quanto pelo clique no véu.
 * @returns O contexto que os demais `Dialog*` leem.
 */
function Dialog(props: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />;
}

/**
 * A caixa em si, já dentro do portal e do véu.
 *
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las — é por aqui que uma caixa
 *   mais larga troca o `w-[560px]`.
 * @param children - O `DialogHeader` e o formulário.
 * @returns O véu com a caixa centrada dentro dele.
 */
function DialogContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay data-slot="dialog-overlay" className={CLASSES_DO_VEU}>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(CLASSES_DA_CAIXA, className)}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
}

/**
 * O bloco do título, com a descrição logo abaixo quando houver uma.
 *
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O bloco que separa o título do conteúdo da caixa.
 */
function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-header" className={cn(CLASSES_DO_CABECALHO, className)} {...props} />
  );
}

/**
 * Os botões da caixa, alinhados à direita.
 *
 * Fica dentro do `<form>` de quem chama, e não fora dele, para que o botão
 * `type="submit"` continue enviando o formulário.
 *
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las.
 * @returns A linha de botões.
 */
function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="dialog-footer" className={cn(CLASSES_DO_RODAPE, className)} {...props} />;
}

/**
 * O título da caixa, que é também o nome acessível dela.
 *
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O `h2` que o Radix liga à caixa por `aria-labelledby`.
 */
function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(CLASSES_DO_TITULO, className)}
      {...props}
    />
  );
}

/**
 * A frase que explica a caixa, lida por leitor de tela logo depois do título.
 *
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O parágrafo que o Radix liga à caixa por `aria-describedby`.
 */
function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(CLASSES_DA_DESCRICAO, className)}
      {...props}
    />
  );
}

export { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle };
